<template>
    <div>
        <ProgressSpinner v-if="loading" stroke-width="1"
            style="position: fixed; width: 250px; height: 250px; top: 30%; left: 50%;" />

        <div v-if="allowEdit && !loading" class="p-d-flex p-flex-row-reverse p-mt-4">
            <Button :label="$t('new')" icon="pi pi-plus" style="width: 100px" @click="addQualification" :disabled="isAddingQualification" />
        </div>

        <Panel :header="$t('education')" :toggleable="true" class="p-mt-1">
            <DataView :value="qualifications" layout="list">
                <template #list="slotProps">
                    <UserQualification class="p-col-12 p-my-2" :qualification="slotProps.data" :disabled="loading"
                        @save="onSave" @delete="onDelete" @cancel-edit="onCancelEdit" @download="onDownload"
                        :edit="slotProps.data.edit || false" :allow-edit="allowEdit" :associates="associates" :userId="userId">
                    </UserQualification>
                </template>
                <template #empty>
                    <span v-if="!loading">
                        {{ $t('noEducationRecords') }}
                    </span>
                </template>
            </DataView>
        </Panel>
    </div>
</template>

<script>
import UserQualification from './UserQualification.vue';
import axios from "axios";
import { File } from "@/utils/file";
import UserService from '@/service/UserService.js';
import OrganizationService from '@/service/OrganizationService';
import UserSession from "@/service/UserSession";

export default {
    components: {
        UserQualification
    },
    props: {
        userId: String,
        allowEdit: Boolean,
    },
    service: null,
    organizationService: null,
    data() {
        const userSession = new UserSession(localStorage);

        return {
            loading: true,
            user: {
            },
            qualifications: [],
            associates: [],
            isOrganizationAdmin: userSession.isOrganizationAdmin,
            organizationId: userSession.organizationId,
        }
    },
    created() {
        this.service = new UserService();
        this.organizationService = new OrganizationService();
    },
    computed: {
        isAddingQualification() {
            return this.qualifications.some(q => q.edit);
        } 
    },
    mounted() {
        this.loadUser(this.userId);
        this.loadAssociates();
    },
    methods: {
        loadUser(id) {
            this.service.get(id)
                .then(data => {
                    data.qualifications
                        .sort((a, b) => a.completionDate.localeCompare(b.completionDate))
                    this.user = data;
                    this.qualifications = data.qualifications;
                    this.loading = false;
                })
                .catch(error => this.error = error[0]);
        },
        loadAssociates() {
            if (this.isOrganizationAdmin) {
                this.organizationService.getAssociates(this.organizationId)
                    .then(data => this.associates = data.data)
            }
        },
        onSave(qualification) {
            this.loading = true;
            Promise.all(
                qualification.userIds.map(userId => 
                    this.service.saveQualification(userId, qualification)
                )
            )
            .then(() => {
                if (qualification.userIds.includes(this.loadUser(this.user.id))) {
                    this.loadUser(this.user.id);
                }
            });
        },
        onCancelEdit(qualification) {
            if (!qualification.id) {
                this.qualifications.splice(this.qualifications.indexOf(qualification), 1);
            }
        },
        onDownload(qualification) {
            axios.get(qualification.file.url, { responseType: 'blob' })
                .then((response) => {
                    const contentDisposition = response.headers['content-disposition'];
                    const fileName = File.extractFileName(contentDisposition);

                    File.download(fileName, response.data);
                });
        },
        onDelete(qualification) {
            this.loading = true;
            this.service.deleteQualification(this.user.id, qualification.id)
                .then(() => this.loadUser(this.user.id))
                .then(() => location.reload());
        },
        addQualification() {
            this.qualifications.push({ edit: true, file: {} });
        }
    }
}
</script>

<style lang="scss" scoped>
::v-deep .p-panel-content {
    border: 0 !important;
    box-shadow: none !important;
    padding: 0 !important;
}

::v-deep .p-dataview-content>div {
    flex-direction: column-reverse;
}
</style>

<i18n lang="yaml" locale="en">
    education: "Education"
    noEducationRecords: "No Education Records"
</i18n>

<i18n lang="yaml" locale="dk">
    education: "Uddannelse"
    noEducationRecords: "Ingen Uddannelsesoptegnelser"
</i18n>